/**
 * Main-thread handle to the field-smoothing Worker (mosaicSmoothWorker.ts) —
 * same request/response/dedupe plumbing shape as fusionDecoder.ts's handle
 * to fusionDecodeWorker.ts, just for one operation (smooth) instead of
 * (decode).
 */
import type { SmoothRequest, SmoothResponse } from "./mosaicSmoothProtocol";

export class MosaicSmoother {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, { resolve: (raw: Uint8Array) => void; reject: (e: Error) => void }>();

  constructor() {
    this.worker = new Worker(new URL("./mosaicSmoothWorker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (ev: MessageEvent<SmoothResponse>) => this.onMessage(ev.data);
  }

  /** Runs mosaicGlFieldSmoothing.ts's despeckle/blur pipeline off the main
   * thread. `raw`'s buffer is COPIED before sending (not transferred
   * directly) — the caller (mosaicGlController.ts) keeps its own decoded
   * grid around to re-smooth from on a later toggle change without a fresh
   * decode, so the original buffer must never be neutered by a transfer. */
  smooth(raw: Uint8Array, width: number, height: number, despeckle: boolean, sigma: number): Promise<Uint8Array> {
    const id = this.nextId++;
    const promise = new Promise<Uint8Array>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
    });
    const copy = raw.slice();
    const req: SmoothRequest = { id, raw: copy.buffer, width, height, despeckle, sigma };
    this.worker.postMessage(req, [copy.buffer]);
    return promise;
  }

  private onMessage(msg: SmoothResponse): void {
    const waiter = this.pending.get(msg.id);
    if (!waiter) return;
    this.pending.delete(msg.id);
    if (!msg.ok) {
      waiter.reject(new Error(msg.error));
      return;
    }
    waiter.resolve(new Uint8Array(msg.raw));
  }

  dispose(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}
